import { Children, type JSX } from 'react';
import * as React from 'react';

import { cn } from '../../v2/lib/utils';
import { IconWarning } from '../icons';
import { MiniIconQuestionMark } from '../mini-icons';
import { Tooltip } from '../tooltip';

interface Props {
  children: React.ReactNode;
  childrenClassName?: string;
  compact?: boolean;
  error?: string | false;
  fieldId: string;
  hideEmptyLabel?: boolean;
  hint?: string;
  horizontal?: boolean;
  inlineLabel?: boolean;
  label?: string;
  renderHelper?: React.ReactNode;
  style?: React.CSSProperties;
  subText?: string;
  tooltip?: string;
  touched?: boolean;
  maxTitleWidthClassName?: string;
}

function shouldShowError(error: Props['error'], touched: Props['touched']): boolean {
  // Show error if the error type is a string and the field is touched
  if (typeof touched !== 'undefined') {
    return typeof error === 'string' && touched;
  }

  // If not, show error if the error type is a string
  if (typeof error === 'string') {
    return true;
  }

  return false;
}

interface AcceptsFieldId extends React.ReactElement<unknown, string | React.JSXElementConstructor<unknown>> {
  fieldId?: string;
}

export function Field({
  children,
  childrenClassName,
  compact,
  error,
  fieldId,
  hideEmptyLabel = false,
  hint,
  horizontal,
  inlineLabel,
  label,
  renderHelper,
  style,
  subText,
  tooltip,
  touched,
  maxTitleWidthClassName,
}: Props): JSX.Element {
  const canInjectFieldId =
    React.isValidElement(children) &&
    !(children.props as AcceptsFieldId).fieldId &&
    Children.count(children) === 1 &&
    typeof children.type !== 'string';
  const showError = shouldShowError(error, touched);

  const containerClassName = horizontal ? 'flex flex-row gap-3 items-center' : 'flex flex-col gap-2';
  const labelRenderHelperClassName = horizontal
    ? 'flex flex-col'
    : `flex flex-row justify-between ${maxTitleWidthClassName}`;
  const labelClassName = horizontal ? 'flex flex-col' : 'flex flex-row items-center gap-1';
  const labelTooltipClassName = horizontal || inlineLabel ? 'flex flex-row items-center gap-1' : '';

  return (
    <div className={containerClassName} key={label} data-testid={fieldId} style={style}>
      {(label || subText || tooltip || renderHelper || !hideEmptyLabel || (showError && compact)) && (
        <div className={labelRenderHelperClassName}>
          <label htmlFor={fieldId}>
            <div className={labelClassName}>
              <div className={labelTooltipClassName}>
                <span
                  className={cn('text-foreground text-base font-semibold', {
                    'text-muted-foreground inline-block text-sm font-normal': compact,
                    'min-h-5': !compact,
                  })}
                >
                  {label}
                </span>
                {tooltip && horizontal ? (
                  <Tooltip message={tooltip} positionTip="left">
                    <MiniIconQuestionMark />
                  </Tooltip>
                ) : null}
                {showError && error && compact ? (
                  <Tooltip message={error} positionTip={horizontal ? 'left' : 'right'}>
                    <IconWarning size={12} />
                  </Tooltip>
                ) : null}
              </div>

              {subText ? (
                <span className={cn('text-foreground text-base font-light', { 'text-sm': compact })}>{subText}</span>
              ) : null}
              {tooltip && !horizontal ? (
                <Tooltip message={tooltip}>
                  <MiniIconQuestionMark />
                </Tooltip>
              ) : null}
            </div>
          </label>
          {renderHelper}
        </div>
      )}
      <div
        className={cn(
          'flex w-auto flex-col gap-2',
          {
            'items-end': compact,
          },
          childrenClassName,
        )}
      >
        {canInjectFieldId ? React.cloneElement(children as React.ReactElement<AcceptsFieldId>, { fieldId }) : children}
        {showError && !compact ? (
          <span
            className={cn('text-fire-600 text-left text-sm', { 'text-right': horizontal })}
            data-testid={`field:error:${fieldId}`}
          >
            {error}
          </span>
        ) : (
          hint && !compact && <span className={cn('text-left text-sm', { 'text-right': horizontal })}>{hint}</span>
        )}
      </div>
    </div>
  );
}
